// components/process-units/Utilities.js
import React, { useState } from 'react';

const Utilities = ({ type, config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Utilities {type === 'rankine' ? '(Rankine Cycle)' : '(Heat Medium)'}</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            {type === 'rankine' ? 
              'The Rankine cycle provides power generation for the plant using waste heat or dedicated fuel. It involves a boiler, steam turbine, condenser, and pump to convert thermal energy into mechanical and electrical energy.' : 
              'The heat medium system circulates a thermal fluid to provide heating to various process units. This system includes heaters, pumps, and distribution piping to maintain consistent temperature control.'}
          </p>
          
          {type === 'rankine' && (
            <div className="parameter-group">
              <div className="parameter-row">
                <label>Steam Cycle Type</label>
                <select 
                  value={config.steamCycleType || 'simple'}
                  onChange={(e) => updateConfig({ steamCycleType: e.target.value })}
                >
                  <option value="simple">Simple Cycle</option>
                  <option value="reheat">Reheat Cycle</option>
                  <option value="regenerative">Regenerative Cycle</option>
                  <option value="combinedCycle">Combined Cycle</option>
                </select>
              </div>
              
              <div className="parameter-row">
                <label>Boiler Pressure (bar)</label>
                <input
                  type="number"
                  min="10"
                  max="150"
                  step="1"
                  value={config.boilerPressure || 60}
                  onChange={(e) => handleInputChange('boilerPressure', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Boiler Temperature (°C)</label>
                <input
                  type="number"
                  min="150"
                  max="600"
                  step="1"
                  value={config.boilerTemperature || 450}
                  onChange={(e) => handleInputChange('boilerTemperature', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Condenser Pressure (bar)</label>
                <input
                  type="number"
                  min="0.05"
                  max="1"
                  step="0.01"
                  value={config.condenserPressure || 0.1}
                  onChange={(e) => handleInputChange('condenserPressure', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Turbine Efficiency (%)</label>
                <input
                  type="number"
                  min="50"
                  max="95"
                  step="1"
                  value={(config.turbineEfficiency || 0.85) * 100}
                  onChange={(e) => handleInputChange('turbineEfficiency', e.target.value / 100)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Pump Efficiency (%)</label>
                <input
                  type="number"
                  min="50"
                  max="95"
                  step="1"
                  value={(config.pumpEfficiency || 0.75) * 100}
                  onChange={(e) => handleInputChange('pumpEfficiency', e.target.value / 100)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Boiler Efficiency (%)</label>
                <input
                  type="number"
                  min="60"
                  max="95"
                  step="1"
                  value={(config.boilerEfficiency || 0.85) * 100}
                  onChange={(e) => handleInputChange('boilerEfficiency', e.target.value / 100)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Heat Source</label>
                <select 
                  value={config.heatSource || 'naturalGas'}
                  onChange={(e) => updateConfig({ heatSource: e.target.value })}
                >
                  <option value="naturalGas">Natural Gas</option>
                  <option value="fuelGas">Fuel Gas (Process)</option>
                  <option value="wasteHeat">Waste Heat Recovery</option>
                  <option value="dualFuel">Dual Fuel</option>
                </select>
              </div>
              
              <div className="parameter-row">
                <label>Power Generation Capacity (MW)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  value={config.powerCapacity || 10}
                  onChange={(e) => handleInputChange('powerCapacity', e.target.value)}
                />
              </div>
            </div>
          )}
          
          {type === 'heatMedium' && (
            <div className="parameter-group">
              <div className="parameter-row">
                <label>Heat Medium Type</label>
                <select 
                  value={config.mediumType || 'hotOil'}
                  onChange={(e) => updateConfig({ mediumType: e.target.value })}
                >
                  <option value="hotOil">Hot Oil</option>
                  <option value="steamSystem">Steam System</option>
                  <option value="glycolWater">Glycol-Water</option>
                </select>
              </div>
              
              <div className="parameter-row">
                <label>System Temperature (°C)</label>
                <input
                  type="number"
                  min="100"
                  max="350"
                  step="5"
                  value={config.systemTemperature || 280}
                  onChange={(e) => handleInputChange('systemTemperature', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Heater Capacity (kW)</label>
                <input
                  type="number"
                  min="100"
                  max="20000"
                  step="100"
                  value={config.heaterCapacity || 5000}
                  onChange={(e) => handleInputChange('heaterCapacity', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Heater Efficiency (%)</label>
                <input
                  type="number"
                  min="50"
                  max="95"
                  step="1"
                  value={(config.heaterEfficiency || 0.85) * 100}
                  onChange={(e) => handleInputChange('heaterEfficiency', e.target.value / 100)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Heat Source</label>
                <select 
                  value={config.heatSource || 'naturalGas'}
                  onChange={(e) => updateConfig({ heatSource: e.target.value })}
                >
                  <option value="naturalGas">Natural Gas</option>
                  <option value="fuelGas">Fuel Gas (Process)</option>
                  <option value="electric">Electric</option>
                  <option value="dualFuel">Dual Fuel</option>
                </select>
              </div>
              
              <div className="parameter-row">
                <label>Circulation Rate (m³/h)</label>
                <input
                  type="number"
                  min="10"
                  max="500"
                  step="5"
                  value={config.circulationRate || 150}
                  onChange={(e) => handleInputChange('circulationRate', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Pump Power (kW)</label>
                <input
                  type="number"
                  min="10"
                  max="500"
                  step="5"
                  value={config.pumpPower || 75}
                  onChange={(e) => handleInputChange('pumpPower', e.target.value)}
                />
              </div>
              
              <div className="parameter-row">
                <label>Heat Loss (%)</label>
                <input
                  type="number"
                  min="1"
                  max="15"
                  step="0.5"
                  value={(config.heatLoss || 0.05) * 100}
                  onChange={(e) => handleInputChange('heatLoss', e.target.value / 100)}
                />
              </div>
            </div>
          )}
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            {type === 'rankine' ? (
              <ul>
                <li>Higher boiler pressures and temperatures increase cycle efficiency but raise capital costs.</li>
                <li>Reheat and regenerative cycles improve efficiency but add complexity.</li>
                <li>Combined cycles can achieve efficiencies over 55% but have higher capital costs.</li>
                <li>Waste heat recovery can significantly reduce fuel consumption and emissions.</li>
                <li>Turbine extraction can provide low-pressure steam for process heating needs.</li>
              </ul>
            ) : (
              <ul>
                <li>Hot oil systems can operate at higher temperatures without high pressures.</li>
                <li>Steam systems provide better heat transfer but require water treatment.</li>
                <li>Glycol-water systems are used for lower temperature applications.</li>
                <li>Circulation rates affect pump power requirements and temperature differentials.</li>
                <li>Insulation quality significantly impacts system efficiency through heat losses.</li>
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Utilities;